import clsx from 'clsx'
import Image from 'next/legacy/image'

import { PAGE_ROUTES, PUBLIC_BETA_BADGE_LINK } from '~/constants'
import { getDownloadButtonLabel, getOSIcon } from '~/lib/download/download'
import { useOSName } from '~/lib/os'
import Button from '~/components/Button/Button'
import Text from '~/components/Text/Text'
import EarlyAccessBadge from '~/components/EarlyAccessBadge/EarlyAccessBadge'
import { BadgeVariant } from '~/components/EarlyAccessBadge/types'

import styles from './DesktopHero.module.sass'

const listItems = [
  'Interact painlessly with APIs',
  'Leverage the power of AI-assisted work',
  'Organize your work in spaces, collections, and tabs',
  'Parametrize requests with variables and environments',
  'Work across devices with auto-save and realtime sync',
  'Build and preview requests without sending them',
  'Use the app offline and without an account',
]

const DesktopHero: React.FC = () => {
  const osName = useOSName()

  return (
    <div className={clsx('site-grid site-container', styles.section)}>
      <div className='col-span-full row-start-2 md:row-start-1 md:col-span-4 lg:col-span-5 lg:col-start-1 xl:col-start-2'>
        <Text className={styles.heading} variant='headline' fontSize={[55, 64, 105]}>
          HTTPIE&nbsp;DESKTOP
          <br />
          &&nbsp;WEB&nbsp;APP
        </Text>

        <EarlyAccessBadge
          variant={BadgeVariant.LABEL}
          className={styles.earlyAccessBadge}
          pingAnimation
          data-track='cta:early-access-badge:from-app-page'
          link={PUBLIC_BETA_BADGE_LINK}
        >
          Public beta
        </EarlyAccessBadge>

        <Text className={styles.subheading} fontSize={[18, 14, 18]}>
          All the user-friendliness of our Terminal version, now wrapped up in a sleek graphical interface.
        </Text>

        <ul className={clsx('dotted-list', styles.list)}>
          {listItems.map((text) => (
            <li key={text}>
              <Text as='span' fontSize={[18, 14, 18]}>
                {text}
              </Text>
            </li>
          ))}
        </ul>

        <div className={styles.buttons}>
          <Button
            className={styles.button}
            icon={getOSIcon(osName)}
            link={PAGE_ROUTES.DOWNLOAD}
            data-track='cta:learn-more-about-app:from-app-page'
          >
            {getDownloadButtonLabel(osName)}
          </Button>

          <Button
            className={styles.button}
            variant='primary'
            link={PAGE_ROUTES.APP}
            data-track='cta:go-to-app:from-app-page'
          >
            Go to Web App →
          </Button>
          <Button link={PAGE_ROUTES.DOCS_DESKTOP} className={styles.button} data-track='cta:read-docs:from-desktop-hero'>
            Read docs
          </Button>
        </div>
      </div>

      <div className='flex justify-center col-span-2 row-start-1 md:col-span-4 lg:col-start-7 lg:col-end-13'>
        <div className={styles.image}>
          <Image src='/Images/app-landing-hero.svg' alt='' layout='responsive' width={220} height={238} priority />
        </div>
      </div>
    </div>
  )
}

export default DesktopHero
